/**
 * IXD 셀 포커스 유틸리티. `useIxdFocus` 가 소유자다.
 *
 * 세 갈래로 나뉘어 있다.
 * - **표시**: `focusCell` / `clearFocusedCell` — 화면을 움직이지 않는다.
 * - **계산**: `planFocusScroll` — 목적지만 정한다. DOM 을 건드리지 않는 순수 함수.
 * - **실행**: `applyFocusScroll` — 계산된 목적지로 스크롤한다.
 *
 * 표시와 스크롤이 갈려 있어야 "이미 보이는 셀은 강조만 하고 화면은 그대로" 가 된다.
 * 둘이 한 함수였을 때는 포커싱할 때마다 화면이 움직였다.
 */

import { ATTR } from "@/utils/dom-attrs";

const DEFAULT_FOCUS_COLOR = "var(--primary-base)";

/** IXD 에서 포커싱 단위가 되는 "표" — DL 영역 컨테이너. */
export const IXD_SECTION_SELECTOR = "[data-dl-id]";

/** DSD 에서 포커싱 단위가 되는 "표" — 표 블록 래퍼(가로 스크롤러를 겸한다). */
export const DSD_SECTION_SELECTOR = `[${ATTR.tableBlock}]`;

/** 포커스 표시를 지운다. ref 는 건드리지 않는다. */
const removeFocusStyle = (cell: HTMLElement | null): void => {
  if (!cell) return;
  cell.style.boxShadow = "";
  cell.style.backgroundColor = "";
};

/**
 * 셀에 포커스 하이라이트를 적용합니다. (inset box-shadow — overflow에 잘리지 않음)
 */
const applyFocusStyle = ({
  cell,
  color,
  bgColor,
}: {
  cell: HTMLElement;
  color: string;
  bgColor?: string;
}): void => {
  cell.style.boxShadow = `inset 0 0 0 2px ${color}`;
  if (bgColor) {
    cell.style.backgroundColor = bgColor;
  }
};

/**
 * 셀을 포커스 상태로 표시한다. 이전 포커스는 해제된다.
 *
 * 화면은 움직이지 않는다 — 어디로 얼마나 스크롤할지는 `planFocusScroll` 이 따로
 * 정한다. 포커스 표시와 스크롤을 분리해야 "이미 보이는 셀은 강조만" 이 가능하다.
 */
export const focusCell = ({
  cell,
  focusedCellRef,
  bgColor,
}: {
  cell: HTMLElement;
  focusedCellRef: React.MutableRefObject<HTMLElement | null>;
  bgColor?: string;
}): void => {
  removeFocusStyle(focusedCellRef.current);
  applyFocusStyle({ cell, color: DEFAULT_FOCUS_COLOR, bgColor });
  focusedCellRef.current = cell;
};

/**
 * 포커스 표시를 지우고 ref 를 비운다.
 *
 * 렌더러 ref 의 `clearFocus()` 와 이름이 겹치지 않게 `Cell` 을 붙였다 — 둘이 나란히
 * 호출되는 자리가 있어(`useIxdFocus.applyFocus`) 같은 이름이면 같은 것으로 읽힌다.
 */
export const clearFocusedCell = (
  focusedCellRef: React.MutableRefObject<HTMLElement | null>,
): void => {
  removeFocusStyle(focusedCellRef.current);
  focusedCellRef.current = null;
};

// ── 스크롤 목적지 계산 ──

/**
 * 셀의 가장 가까운 수평 스크롤 부모(표마다 붙는 가로 스크롤 래퍼)를 찾는다.
 * 없으면 세로 컨테이너가 가로도 겸한다 — 못 찾았다고 가로 노출을 포기하지 않는다.
 */
const horizontalScrollerOf = (
  cell: HTMLElement,
  container: HTMLElement,
): HTMLElement => {
  let current = cell.parentElement;
  while (current && current !== container) {
    // 넘친다(scrollWidth > clientWidth)와 스크롤된다는 다르다 — overflow-x 가
    // visible/clip 인 조상도 콘텐츠가 넓으면 넘치고, 거기에 scrollTo 를 걸면 아무 일도
    // 일어나지 않은 채 진짜 스크롤러까지 못 올라간다.
    const { overflowX } = getComputedStyle(current);
    if (
      (overflowX === "auto" || overflowX === "scroll") &&
      current.scrollWidth > current.clientWidth + 1
    ) {
      return current;
    }
    current = current.parentElement;
  }
  return container;
};

const clamp = (value: number, max: number): number =>
  Math.min(Math.max(value, 0), Math.max(max, 0));

/**
 * 한 축에서 셀을 보이게 하는 **최소** 스크롤 위치. 이미 보이면 null.
 *
 * 스크롤 영역보다 큰 셀은 온전히 담길 수 없어 "시작점이 보이면 됐다" 로 요구를
 * 낮춘다. 그러지 않으면 영원히 안 보임으로 판정돼 클릭마다 화면이 움직인다.
 * 그때도 시작점은 **뷰포트 안**이어야 한다 — 시작점이 반대편 끝을 지나 있으면
 * 화면에 아무것도 없는데 보인다고 답하게 된다.
 *
 * 목적지는 벗어난 **방향**으로 갈린다. 앞쪽(위/왼쪽)으로 벗어났으면 시작을 맞추는 것이
 * 곧 최소 이동이다 — 여기서 끝 맞춤을 함께 고려하면 뷰포트 하나만큼 더 움직여 셀이
 * 반대편 경계에 붙는다(가로 스크롤바가 `sticky bottom-0` 이라 가려지기도 한다).
 * 뒤쪽으로 벗어났을 때만 끝 맞춤이 최소이고, 큰 셀은 시작점에서 멈춘다.
 */
const revealOffset = (args: {
  cellStart: number;
  cellSize: number;
  scrollerStart: number;
  scrollerSize: number;
  scrollOffset: number;
}): number | null => {
  const { cellStart, cellSize, scrollerStart, scrollerSize, scrollOffset } =
    args;
  const scrollerEnd = scrollerStart + scrollerSize;
  const startVisible = cellStart >= scrollerStart && cellStart < scrollerEnd;
  const visible =
    cellSize > scrollerSize
      ? startVisible
      : startVisible && cellStart + cellSize <= scrollerEnd;
  if (visible) return null;
  const offsetStart = cellStart - scrollerStart + scrollOffset;
  if (cellStart < scrollerStart) return offsetStart;
  return Math.min(offsetStart, offsetStart + cellSize - scrollerSize);
};

export interface FocusScrollPlan {
  /** 세로 스크롤 목적지. null 이면 세로로는 움직이지 않는다. */
  vertical: { container: HTMLElement; top: number } | null;
  /** 가로 스크롤 목적지. 세로와 다른 엘리먼트일 수 있다. */
  horizontal: { container: HTMLElement; left: number } | null;
}

/**
 * 어떤 식으로 스크롤을 할 지 목적지를 계산하는 함수. 실제 스크롤은 하지 않는다.
 *
 * 두 축이 서로 다른 엘리먼트를 스크롤하므로 축별로 목적지를 따로 낸다. 축별로
 * 독립해 null 이 될 수 있다 — 같은 행의 다른 기간 열을 클릭하면 가로만 움직여야 한다.
 *
 * 세로는 **이미 보이는 셀이면 움직이지 않는다** — 표가 바뀌어도 그렇다. 기준선은
 * "움직여야 할 때 어디까지" 만 정한다.
 *
 * `sectionSelector` 를 주면 세로에 "표" 기준선이 생긴다. 셀이 안 보여서 움직이는데
 * 포커싱 대상 표까지 바뀌었으면 표 머리를 위에 두되, **그 위치에서도 셀이 안 보이면**
 * 셀이 보일 때까지만 더 내린다 — 표 머리 아래로만 밀리고 위로 되돌아가지 않는다
 * (표→셀 두 번 움직이는 것으로 보이지 않게). 그 "안 보이면" 판정은 **표 머리로 옮긴
 * 뒤** 위치로 한다. 같은 표 안에서는 표 머리를 잊는다.
 *
 * 안 주면 언제나 "셀이 보이게" 규칙만 쓴다. 방향키 이동처럼 한 칸씩 옮기는 조작에서는
 * 표 경계를 넘을 때마다 표 머리로 점프하면 커서 이동 감각이 깨진다. 가로에는 애초에
 * 그런 기준선이 없어 항상 후자다.
 */
export const planFocusScroll = (args: {
  cell: HTMLElement;
  prevCell: HTMLElement | null;
  container: HTMLElement;
  sectionSelector?: string;
}): FocusScrollPlan => {
  const { cell, prevCell, container, sectionSelector } = args;
  const containerRect = container.getBoundingClientRect();
  const cellRect = cell.getBoundingClientRect();

  const revealTop = revealOffset({
    cellStart: cellRect.top,
    cellSize: cellRect.height,
    scrollerStart: containerRect.top,
    scrollerSize: container.clientHeight,
    scrollOffset: container.scrollTop,
  });

  const section = sectionSelector
    ? cell.closest<HTMLElement>(sectionSelector)
    : null;
  const sectionChanged =
    !!sectionSelector &&
    section !== (prevCell?.closest<HTMLElement>(sectionSelector) ?? null);
  const sectionTop =
    sectionChanged && section
      ? section.getBoundingClientRect().top -
        containerRect.top +
        container.scrollTop
      : null;

  const maxTop = container.scrollHeight - container.clientHeight;
  /**
   * 세로 목적지. 세 갈래다.
   *
   * 1. 셀이 이미 보인다 → 안 움직인다. 표가 바뀌었어도 그렇다.
   * 2. 움직여야 하는데 표는 그대로다 → 셀이 보이는 최소 이동.
   * 3. 움직여야 하고 표도 바뀌었다 → 표 머리를 위에 두되, **표 머리로 옮긴 뒤** 에도
   *    셀이 안 보이면 보일 때까지만 더 내린다. 그 판정에 `revealTop`(= 지금 위치 기준)
   *    을 쓰면 안 된다 — 표 머리는 셀보다 한 화면 넘게 위일 수 있다(IXD 는 DL 하나에
   *    당기 표 다음 전기 표를 쌓는다). 그래서 `revealOffset` 을 옮긴 뒤 좌표로 다시
   *    부른다. 가시성 규칙(뷰포트보다 큰 셀은 시작점만, 아니면 전체)을 두 벌로 두지
   *    않으려는 것이다.
   *
   * `Math.max` 는 "표 머리 위로는 올라가지 않는다" 는 불변식이다. 셀이 자기 표 머리보다
   * 위에 있을 수 없어 실제로는 걸리지 않지만, 식에 박아 두면 읽는 쪽이 확인할 필요가
   * 없다.
   */
  const top =
    // 이미 보이는 셀에서는 아무것도 하지 않는다 — 표가 바뀌어도. 표 머리를 보여주려고
    // 보이는 셀에서 화면을 움직이면 포커싱한 셀이 오히려 밖으로 나갈 수 있다(IXD 는 DL
    // 하나에 기간별 표를 쌓는다 — 당기 표 다음 전기 표). 표 기준선은 "움직여야 할 때
    // 어디까지" 만 정한다.
    revealTop === null
      ? null
      : sectionTop === null
        ? revealTop
        : Math.max(
            sectionTop,
            revealOffset({
              // 표 머리로 옮긴 뒤의 셀 화면 좌표.
              cellStart: cellRect.top - (sectionTop - container.scrollTop),
              cellSize: cellRect.height,
              scrollerStart: containerRect.top,
              scrollerSize: container.clientHeight,
              scrollOffset: sectionTop,
            }) ?? sectionTop,
          );

  const hScroller = horizontalScrollerOf(cell, container);
  const hScrollerRect = hScroller.getBoundingClientRect();
  const revealLeft = revealOffset({
    cellStart: cellRect.left,
    cellSize: cellRect.width,
    scrollerStart: hScrollerRect.left,
    scrollerSize: hScroller.clientWidth,
    scrollOffset: hScroller.scrollLeft,
  });

  return {
    vertical: top === null ? null : { container, top: clamp(top, maxTop) },
    horizontal:
      revealLeft === null
        ? null
        : {
            container: hScroller,
            left: clamp(
              revealLeft,
              hScroller.scrollWidth - hScroller.clientWidth,
            ),
          },
  };
};

/**
 * 셀을 화면의 `alignTop`(뷰포트 Y) 높이에 맞추는 목적지. 가로는 셀이 보이게만 한다.
 *
 * WHY: 값 위치 검토는 DSD 줄과 대응 IXD 칸을 같은 높이에 둔다 — 비교할 때 시선이 수평으로만
 * 움직이게 하려는 것이다. 끝까지 내려도 못 맞추는 높이는 스크롤 한계에서 멈춘다.
 */
export const planAlignedScroll = (args: {
  cell: HTMLElement;
  container: HTMLElement;
  alignTop: number;
}): FocusScrollPlan => {
  const { cell, container, alignTop } = args;
  const base = planFocusScroll({ cell, prevCell: null, container });
  const delta = cell.getBoundingClientRect().top - alignTop;
  return {
    ...base,
    vertical: {
      container,
      top: clamp(
        container.scrollTop + delta,
        container.scrollHeight - container.clientHeight,
      ),
    },
  };
};

/**
 * `element` 의 스크롤이 멈춘 뒤 `run` 을 한 번 부른다. 스크롤이 일어나지 않으면 잠깐 뒤에,
 * 오래 이어져도 `maxWaitMs` 뒤에는 부른다.
 *
 * WHY: smooth 스크롤 도중에 위치를 재면 출발점을 재게 된다. 다른 패널을 이 높이에 맞추려면
 * 이 패널이 멈춘 뒤 재야 한다.
 */
export const afterScrollSettles = (
  element: HTMLElement,
  run: () => void,
  { idleMs = 120, maxWaitMs = 800 } = {},
): void => {
  let done = false;
  let idle = window.setTimeout(finish, idleMs);
  const cap = window.setTimeout(finish, maxWaitMs);
  function onScroll() {
    window.clearTimeout(idle);
    idle = window.setTimeout(finish, idleMs);
  }
  function finish() {
    if (done) return;
    done = true;
    element.removeEventListener("scroll", onScroll);
    window.clearTimeout(idle);
    window.clearTimeout(cap);
    run();
  }
  element.addEventListener("scroll", onScroll, { passive: true });
};

/** 계산된 목적지로 스크롤한다. 축별로 목적지가 없으면 그 축은 움직이지 않는다. */
export const applyFocusScroll = (plan: FocusScrollPlan): void => {
  // 같은 엘리먼트에 scrollTo 를 두 번 걸면 뒤 호출이 앞의 smooth 스크롤을 취소하고,
  // 지정하지 않은 축을 "현재 위치"(= 아직 움직이지 않은 시작점)로 되돌린다. 가로 래퍼를
  // 못 찾아 세로 컨테이너가 가로를 겸할 때 바로 걸리므로 한 번에 두 축을 지정한다.
  if (
    plan.vertical &&
    plan.horizontal &&
    plan.vertical.container === plan.horizontal.container
  ) {
    plan.vertical.container.scrollTo({
      top: plan.vertical.top,
      left: plan.horizontal.left,
      behavior: "smooth",
    });
    return;
  }
  plan.vertical?.container.scrollTo({
    top: plan.vertical.top,
    behavior: "smooth",
  });
  plan.horizontal?.container.scrollTo({
    left: plan.horizontal.left,
    behavior: "smooth",
  });
};
